import { useTranslation } from 'react-i18next'
import { UseQueryResult } from '@tanstack/react-query'
import { z } from 'zod'
import { buildSyncFieldsValidationSchema } from '@/features/menu-management/helpers/buildSyncFieldsValidationSchema'
import {
  AutoTranslateButton,
  AutoTranslateFormEffect
} from '@/features/menu-management/helpers/AutoTranslateFormEffect'
import { ImageType } from '@/shared/api/BaseAPI.model'
import IMAGE_SLOTS_KEYS from '@/shared/store/imageSlots.model'
import { useSelectedLanguagesQuery } from '../hooks/useSelectedLanguagesQuery'
import { useDishSizesQuery } from '../hooks/useDishSizesQuery'
import { CATEGORY_IMAGES_ENABLED } from '../config'

type CategoryImageProps = {
  imageQuery?: UseQueryResult<ImageType, Error>
  onImageDelete?: () => void
}

export const useDishCategoryFormConfig = (
  categoryImage?: CategoryImageProps
) => {
  const { t } = useTranslation()
  const currentLanguage =
    useTranslation().i18n.language || localStorage.getItem('i18nextLng') || ''
  const { defaultLanguageOption, selectedLanguagesOptions } =
    useSelectedLanguagesQuery(currentLanguage)

  const { dishSizesOptions } = useDishSizesQuery()

  const filteredSelectedLanguagesOptions = selectedLanguagesOptions.filter(
    selectedLanguage => selectedLanguage.value !== defaultLanguageOption.value
  )

  // Hidden until the backend can store it, see SidebarAccordion/config.ts.
  const imageField = CATEGORY_IMAGES_ENABLED
    ? [
        {
          name: 'categoryImage',
          type: 'image',
          label: t('SIDEBAR_ACCORDION.CATEGORY.IMAGE'),
          modalId: IMAGE_SLOTS_KEYS.CATEGORY_IMAGE,
          imageQuery: categoryImage?.imageQuery,
          onImageDelete: categoryImage?.onImageDelete
        }
      ]
    : []

  const formConfig = {
    id: 'dish-category-form',
    FormEffects: <AutoTranslateFormEffect />,
    // Rendered by the modal in its action row rather than as a form field:
    // a translation helper belongs beside submit and cancel, not wedged
    // between the form's own inputs.
    footerAction: (
      <AutoTranslateButton
        defaultLanguage={defaultLanguageOption?.value || 'en'}
        allLanguages={selectedLanguagesOptions.map(o => o.value as string)}
        syncFieldNames={['translations']}
      />
    ),
    fields: [
      ...imageField,
      {
        name: 'dishSizes',
        label: t('SIDEBAR_ACCORDION.CATEGORY.DISH_SIZES'),
        placeholder: t('SIDEBAR_ACCORDION.CATEGORY.DISH_SIZES_PLACEHOLDER'),
        focused: true,
        type: 'multi-select',
        options: dishSizesOptions
      },
      {
        name: `translations.${defaultLanguageOption?.value}`,
        label: defaultLanguageOption?.label,
        focused: true,
        type: 'text',
        required: true
      },
      {
        name: 'translations',
        type: 'input-group',
        inputGroupDirection: 'column',
        // One field per enabled language: without its own scroll box the group
        // makes the dialog taller than the viewport.
        scrollable: true,
        inputs: [
          ...filteredSelectedLanguagesOptions.map(language => ({
            name: language.value,
            label: language.label,
            focused: true,
            type: 'text',
            validation: z
              .string({
                required_error: t(
                  'AUTHORIZATION.LOGIN.EMAIL.VALIDATION.REQUIRED'
                )
              })
              .trim()
          }))
        ]
      }
    ],
    validationSchema: z.object({
      translations: buildSyncFieldsValidationSchema(selectedLanguagesOptions),
      dishSizes: z
        .array(
          z.object({
            // Display-only, and a dish size with no translation in the default
            // language has none, requiring it would block the save with an
            // error the form has nowhere to show.
            label: z.string().optional(),
            value: z.string()
          })
        )
        .optional()
    })
  }
  return formConfig
}
